import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Hook to check if the current user (as an apprentice) has been approved to tattoo.
 * Returns true if they have an active apprenticeship profile with can_tattoo = true.
 */
export function useApprenticeCanTattoo() {
  const { user, currentShopId, isApprentice } = useAuth();

  const query = useQuery({
    queryKey: ["apprentice-can-tattoo", user?.id, currentShopId],
    queryFn: async () => {
      if (!user?.id || !currentShopId) return false;

      const { data, error } = await supabase
        .from("apprenticeship_profiles")
        .select("can_tattoo")
        .eq("artist_id", user.id)
        .eq("shop_id", currentShopId)
        .eq("status", "active")
        .maybeSingle();

      if (error) {
        console.error("Error checking apprentice tattoo status:", error);
        return false;
      }

      return data?.can_tattoo === true;
    },
    enabled: !!user?.id && !!currentShopId && isApprentice,
    staleTime: 30000, // Cache for 30 seconds
  });

  // If not an apprentice, this doesn't apply
  if (!isApprentice) {
    return { canTattoo: false, isLoading: false };
  }

  return {
    canTattoo: query.data ?? false,
    isLoading: query.isLoading,
  };
}
